import React, { useCallback } from 'react';
import { styled } from '@compiled/react';
import { useRelayEnvironment } from 'react-relay';
import { token } from '@atlaskit/tokens';
import { useProjectMenuRecommendations } from '@atlassian/jira-atlassian-navigation-recommendations/src/controllers/project-menu-recommendations/index.tsx';
import { projectsMenuDropdownRecommendationSelector } from '@atlassian/jira-atlassian-navigation-recommendations/src/experiences.tsx';
import { recommendations } from '@atlassian/jira-atlassian-navigation-recommendations/src/ui/index.tsx';
import { useExperienceStart } from '@atlassian/jira-experience-tracker/src/ui/experience-start/index.tsx';
import { useFavoriteProjects as useFavorite } from '@atlassian/jira-favorites-store/src/index.tsx';
import { useIntl } from '@atlassian/jira-intl';
import { useCreateProjectPermission } from '@atlassian/jira-nav-item-service/src/services/create-project-permisson/index.tsx';
import { MENU_ID } from '@atlassian/jira-navigation-apps-common/src/constants.tsx';
import { fireUIAnalytics, useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import { useRecentProjects as useRecent } from '@atlassian/jira-recents-store/src/index.tsx';
import { useGlobalRefStoreActions } from '@atlassian/jira-refs-store/src/index.tsx';
import { useAccountId } from '@atlassian/jira-tenant-context-controller/src/components/account-id/index.tsx';
import { useAppEditions } from '@atlassian/jira-tenant-context-controller/src/components/app-editions/index.tsx';
import { useIsAnonymous } from '@atlassian/jira-tenant-context-controller/src/components/is-anonymous/index.tsx';
import { useLocale } from '@atlassian/jira-tenant-context-controller/src/components/locale/index.tsx';
import { useTenantContext } from '@atlassian/jira-tenant-context-controller/src/components/tenant-context/index.tsx';
import { isFedRamp } from '@atlassian/atl-context';
import {
	DEFAULT_LOAD_COUNT_FAV,
	DEFAULT_LOAD_COUNT_RECENT,
	EXPERIENCE_NAVIGATION_TOP_MENU,
	NAVIGATION_ITEM_ID,
} from '../../common/constants.tsx';
import { TopLevelErrorBoundary } from '../../common/ui/error-boundary/index.tsx';
import { MenuTrigger } from '../../common/ui/menu/trigger/main.tsx';
import { Popup } from '../../common/ui/popup/main.tsx';
import { Menu } from './menu/main.tsx';
import messages from './messages.tsx';

const ProjectsAnonymous = () => {
	const { formatMessage } = useIntl();
	const baseUrl = '';

	return (
		<MenuTrigger
			href={`${baseUrl}/jira/projects`}
			menuId={MENU_ID.PROJECTS}
			navigationItemId={NAVIGATION_ITEM_ID.PROJECTS}
		>
			{formatMessage(messages.projects)}
		</MenuTrigger>
	);
};

const ProjectsLoggedIn = () => {
	const { formatMessage } = useIntl();

	const locale = useLocale();
	const accountId = useAccountId();
	const isInFedRamp = isFedRamp();
	const { cloudId, licensedProducts, firstActivationDateMs, areProductSuggestionsEnabled } =
		useTenantContext();
	const [, { register }] = useGlobalRefStoreActions();
	const [{ hasFetchedOnce: hasFetchedOnceFavorites }, { loadFavoriteProjects: loadFavorite }] =
		useFavorite();
	const [, { loadRecentProjects: loadRecent }] = useRecent();

	const [, { loadRecommendations }] = useProjectMenuRecommendations();
	const { software: edition } = useAppEditions();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const environment = useRelayEnvironment();
	const [{ hasSuccessOnce: hasFetchedCreateProjectPermissions, canCreateProject }] =
		useCreateProjectPermission();
	const onStart = useExperienceStart({
		experience: EXPERIENCE_NAVIGATION_TOP_MENU,
		experienceId: NAVIGATION_ITEM_ID.PROJECTS,
		analyticsSource: 'atlassian-navigation',
	});

	const onBeforeMenuOpen = useCallback(() => {
		onStart();
		projectsMenuDropdownRecommendationSelector.start();

		// refresh recents on open to add recently visited pages
		loadRecent(DEFAULT_LOAD_COUNT_RECENT);
		if (accountId) {
			fireUIAnalytics(createAnalyticsEvent({}), 'dropdown viewed', 'projectsMenuDropdown');
			if (hasFetchedCreateProjectPermissions) {
				loadRecommendations(
					recommendations,
					{
						locale,
						cloudId,
						accountId,
						licensedProducts,
						edition,
						firstActivationDateMs,
						areProductSuggestionsEnabled,
						environment,
						canCreateProject,
						isInFedRamp,
					},
					createAnalyticsEvent({}),
				);
			}
		}
		// load favourites only if not loaded already (optimisation to reduce requests)
		if (!hasFetchedOnceFavorites) loadFavorite(DEFAULT_LOAD_COUNT_FAV);
	}, [
		onStart,
		loadRecent,
		accountId,
		hasFetchedOnceFavorites,
		loadFavorite,
		createAnalyticsEvent,
		hasFetchedCreateProjectPermissions,
		loadRecommendations,
		locale,
		cloudId,
		licensedProducts,
		edition,
		firstActivationDateMs,
		areProductSuggestionsEnabled,
		environment,
		canCreateProject,
		isInFedRamp,
	]);

	const onRef = useCallback(
		(ref: HTMLElement | null) => {
			// We want the last of these nodes, but can spotlight as a fallback any intermediaries
			// It may cause the spotlight border to be bigger than intended (around the entire block element)
			// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
			const refToFirstChild = ref && (ref.firstChild as HTMLElement);
			const refToFirstGrandchild =
				// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
				refToFirstChild && (refToFirstChild.firstChild as HTMLElement);
			const refToFirstGreatGrandchild =
				// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
				refToFirstGrandchild && (refToFirstGrandchild.firstChild as HTMLElement);
			register(
				`NAVIGATION_${MENU_ID.PROJECTS}`,
				refToFirstGreatGrandchild || refToFirstGrandchild || refToFirstChild || ref,
			);
		},
		[register],
	);

	return (
		<Wrapper ref={onRef}>
			<Popup
				menuId={MENU_ID.PROJECTS}
				navigationItemId={NAVIGATION_ITEM_ID.PROJECTS}
				Menu={Menu}
				onBeforeMenuOpen={onBeforeMenuOpen}
				label={formatMessage(messages.projects)}
			>
				{formatMessage(messages.projects)}
			</Popup>
		</Wrapper>
	);
};

const ProjectWithAuth = () => {
	const isAnonymous = useIsAnonymous();

	return isAnonymous ? <ProjectsAnonymous /> : <ProjectsLoggedIn />;
};

export const Projects = () => (
	<TopLevelErrorBoundary id={MENU_ID.PROJECTS}>
		<ProjectWithAuth />
	</TopLevelErrorBoundary>
);

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled, @atlaskit/ui-styling-standard/no-exported-styles -- Ignored via go/DSP-18766
export const Wrapper = styled.div({
	color: token('color.text'),
	height: '100%',

	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	'> [role="listitem"]': {
		height: '100%',
	},

	alignContent: 'center',

	/* reset margin for SSR because ak/navigation container adds margins twice */
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	...(__SERVER__ && { '&&&': { margin: 0 } }),
});
